/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";

const HcmDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const hcmAlarms = parsedAttributes.alarms;
  const hcmAlarmsMask = parsedAttributes.alarmsMask;
  const agcSwitch = parsedAttributes.agcSwitch;

  const hasAlarms = hcmAlarms !== undefined || hcmAlarmsMask !== undefined;
  const hasVersionInfo = device.swVersion || device.hwVersion;
  const hasAdditionalInfo = agcSwitch !== undefined;

  if (!hasAlarms && !hasVersionInfo && !hasAdditionalInfo) return null;

  // HCM Alarm definitions from MIB (bits 0-4, 0-indexed)
  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "HW Failure" },
    { bit: 1, name: "Adjustment Fault" },
    { bit: 2, name: "Installation Fault" },
    { bit: 3, name: "SW Release Mismatch" },
    { bit: 4, name: "Connectivity" },
  ];

  const isDisconnected = statusLabel === "Disconnected";

  // Format AGC switch value
  const formatAgcSwitch = (value: any) => {
    if (value === undefined || value === null) return undefined;
    const val = String(value);
    switch (val) {
      case "0":
        return "Off";
      case "1":
        return "On";
      default:
        return val;
    }
  };

  // If alarmsOnly mode, only render alarm sections
  if (alarmsOnly) {
    if (!hasAlarms) return null;
    
    return (
      <>
        {hasAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={hcmAlarms}
                alarmsMask={hcmAlarmsMask}
                alarmDefinitions={alarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  // Normal mode: show configuration only, no alarms
  if (!hasAdditionalInfo) return null;

  return (
    <>
      {hasAdditionalInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Configuration</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {agcSwitch !== undefined && (
              <DetailRow label="AGC Switch" value={formatAgcSwitch(agcSwitch)} />
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default HcmDeviceDetails;
